/**
 * One-line summaries of a workflow action and its rules, for the folded rule cards and the canvas blocks
 * of the Workflow2 redesign (2026-10-10).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Plan: src/pages/Workflow2/PROJECT_NOTES.md (Workflow UI overhaul, step P1). Wording follows the design's `prevOf`
 * ('3 task rules', '2 text rules · new patients', '<board> · <card action>', 'Not set up yet') and its
 * rule summaries ('D0120 → Jane · Due 1 day after…', 'D0120 → Template · Immediately'). The inputs are the
 * classic builder's real `properties` (WorkflowBuilder.vue: initializeNodeProperties, addTaskRule,
 * addSmsRule, addEmailRule, addNewPatientSmsRule, getNodePreview):
 *
 *   task rule      { procCodes: [{value,text}], assignees: [{displayName}], description, dueDateObj: {value,text},
 *                    dueDate, wikis, tagsList }
 *   message rule   { procCodes, templateObj: {Name}, templateId, sendWhenObj: {value,text}, sendWhen, smsText }
 *   new-pt rule    { procCodes, providerObjs: [{displayName}], providerNums, templateObj, templateId }
 *
 * A saved workflow keeps those whole rule objects (the Obj fields included), so a loaded rule reads the
 * same as a fresh one. A rule from an OLD v1 workflow only has ids (codeNum, assignedToEmpID, the template
 * ID, scheduleWhen / taskDueDate); the ids are not readable, so those show as counts ('2 procedure codes').
 *
 * A rule is EMPTY when nothing but the defaults is set — the default due / send time is 'immediately',
 * which does not count. An action with only empty rules is "Not set up yet", and an empty rule has no summary.
 *
 * Pure, and never throws on null / undefined / garbage inputs.
 */

const NOT_SET = 'Not set up yet';
const DEFAULT_WHEN = 'immediately';

function isObject(v) {
  return v !== null && typeof v === 'object';
}

function str(v) {
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' && isFinite(v)) return String(v);
  return '';
}

function has(v) {
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number') return isFinite(v);
  if (typeof v === 'boolean') return v;
  return isObject(v);
}

function asArray(v) {
  if (Array.isArray(v)) return v.filter(x => x !== null && x !== undefined && x !== '');
  return has(v) ? [v] : [];
}

function count(n, one, many) {
  return n + ' ' + (n === 1 ? one : many || one + 's');
}

function lowerFirst(s) {
  return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

// An id like 4821 says nothing to a person; a string like 'D0120' or 'Whoever marked Apt Complete' does.
function readable(v) {
  const s = str(v);
  return s !== '' && !/^\d+$/.test(s) ? s : '';
}

// Names when the items carry them, else "N things" so a configured rule never reads as blank.
function namesOrCount(items, nameOf, one, many) {
  if (items.length === 0) return [];
  const names = items.map(nameOf).filter(Boolean);
  return names.length ? names : [count(items.length, one, many)];
}

function snippet(text) {
  const line = str(text).split(/\r?\n/)[0].trim();
  return line.length > 40 ? line.slice(0, 40).trim() + '…' : line;
}

// ── labels ─────────────────────────────────────────────────────────────────────────────────────────
// 'D0120 - Periodic exam' → 'D0120'
function codeName(c) {
  if (isObject(c)) return str(c.text).split(' - ')[0].trim();
  return readable(c);
}

function codeLabels(rule) {
  const items = asArray(rule.procCodes);
  return namesOrCount(items.length ? items : asArray(rule.codeNum), codeName, 'procedure code');
}

function personName(e) {
  if (isObject(e)) {
    return str(e.displayName) || str(e.Name) || (str(e.FirstName) + ' ' + str(e.LastName)).trim() || readable(e.EmployeeID);
  }
  return readable(e);
}

function assigneeLabels(rule) {
  let items = asArray(rule.assignees);
  if (!items.length && isObject(rule.assigneeObj)) items = [rule.assigneeObj];
  if (!items.length) items = asArray(rule.assignedToEmpID);
  return namesOrCount(items, personName, 'person', 'people');
}

function providerName(p) {
  return isObject(p) ? str(p.displayName) || (str(p.FName) + ' ' + str(p.LName)).trim() : '';
}

function providerLabels(rule) {
  const items = asArray(rule.providerObjs);
  return namesOrCount(items.length ? items : asArray(rule.providerNums), providerName, 'provider');
}

function templateLabel(rule) {
  if (isObject(rule.templateObj)) {
    const name = str(rule.templateObj.Name) || str(rule.templateObj.name) || str(rule.templateObj.Title);
    if (name) return name;
  }
  if (has(rule.templateId) || has(rule.smsTemplateID) || has(rule.emailTemplateID) || isObject(rule.templateObj)) return 'Template chosen';
  return snippet(rule.smsText);
}

// The send / due time as a stored value, then as words when only the value survived (the trigger-specific
// wording lives in the option objects, which a fresh or re-opened rule carries).
function whenValue(obj, ...values) {
  if (isObject(obj) && has(obj.value)) return str(obj.value);
  for (let i = 0; i < values.length; i++) {
    const v = Array.isArray(values[i]) ? values[i][0] : values[i];
    if (has(v)) return str(v);
  }
  return '';
}

function whenWords(value) {
  if (value === DEFAULT_WHEN) return 'Immediately';
  if (value === 'odoa') return 'On the day of the appointment';
  const m = /^(\d+)(day|days|week|weeks)(b?)$/.exec(value);
  if (!m) return '';
  return count(Number(m[1]), m[2].replace(/s$/, '')) + (m[3] ? ' before' : ' after');
}

function whenLabel(obj, value) {
  if (isObject(obj) && str(obj.text)) return str(obj.text);
  return whenWords(value);
}

// ── rules ──────────────────────────────────────────────────────────────────────────────────────────
function taskFacts(rule) {
  const dueValue = whenValue(rule.dueDateObj, rule.dueDate, rule.taskDueDate);
  return {
    codes: codeLabels(rule),
    who: assigneeLabels(rule),
    description: str(rule.description) || str(rule.task),
    hasWikis: has(rule.wikis) || has(rule.wikiPageID),
    hasTags: has(rule.tagsList) || has(rule.tags) || has(rule.taskTag),
    dueValue,
    dueLabel: whenLabel(rule.dueDateObj, dueValue)
  };
}

function messageFacts(rule) {
  const whenVal = whenValue(rule.sendWhenObj, rule.sendWhen, rule.scheduleWhen);
  return {
    codes: codeLabels(rule),
    template: templateLabel(rule),
    whenValue: whenVal,
    when: whenLabel(rule.sendWhenObj, whenVal)
  };
}

function newPatientFacts(rule) {
  return {
    codes: codeLabels(rule),
    providers: providerLabels(rule),
    template: templateLabel(rule)
  };
}

/**
 * True when nothing but the defaults is set.
 * @param {Object} rule
 * @param {string} kind 'task' | 'sms' | 'email' | 'message' (sms and email rules read the same) | 'newpatient'
 */
export function isRuleEmpty(rule, kind) {
  if (!isObject(rule)) return true;
  if (kind === 'task') {
    const f = taskFacts(rule);
    return !f.codes.length && !f.who.length && !f.description && !f.hasWikis && !f.hasTags &&
      (f.dueValue === '' || f.dueValue === DEFAULT_WHEN);
  }
  if (kind === 'sms' || kind === 'email' || kind === 'message') {
    const f = messageFacts(rule);
    return !f.codes.length && !f.template && (f.whenValue === '' || f.whenValue === DEFAULT_WHEN);
  }
  if (kind === 'newpatient') {
    const f = newPatientFacts(rule);
    return !f.codes.length && !f.providers.length && !f.template;
  }
  return Object.keys(rule).length === 0;
}

/** 'D0120, D1110 → Jane Doe · Due 1 day after appointment is completed'; '' for an empty rule. */
export function taskRuleSummary(rule) {
  if (isRuleEmpty(rule, 'task')) return '';
  const f = taskFacts(rule);
  let out = f.codes.join(', ') || snippet(f.description);
  if (f.who.length) out += (out ? ' → ' : '') + f.who.join(', ');
  if (f.dueLabel) out += (out ? ' · ' : '') + 'Due ' + lowerFirst(f.dueLabel);
  return out;
}

/** 'D0120 → Recall text · Immediately' (a text or an email rule); '' for an empty rule. */
export function messageRuleSummary(rule) {
  if (isRuleEmpty(rule, 'message')) return '';
  const f = messageFacts(rule);
  let out = f.codes.join(', ');
  if (f.template) out += (out ? ' → ' : '') + f.template;
  if (f.when) out += (out ? ' · ' : '') + f.when;
  return out;
}

/** 'D0120 · Dr. Smith → New patient text' (a "for new patients only" procedure rule); '' for an empty rule. */
export function newPatientRuleSummary(rule) {
  if (isRuleEmpty(rule, 'newpatient')) return '';
  const f = newPatientFacts(rule);
  let out = f.codes.concat(f.providers.length ? [f.providers.join(', ')] : []).join(' · ');
  if (f.template) out += (out ? ' → ' : '') + f.template;
  return out;
}

// ── blocks ─────────────────────────────────────────────────────────────────────────────────────────
const CARD_ACTIONS = {
  add: 'Add new card',
  addcard: 'Add new card',
  update: 'Update existing card',
  updatecard: 'Update existing card',
  move: 'Move card to column',
  movecardtocolumn: 'Move card to column'
};

const MANGO_EVENTS = { missed_call: 'Missed call', voicemail: 'Voicemail', call_ended: 'Call ended' };

function ok(text) {
  return { text, tone: 'ok' };
}

function warn() {
  return { text: NOT_SET, tone: 'warn' };
}

function rulesIn(rules, kind) {
  return (Array.isArray(rules) ? rules : []).filter(r => !isRuleEmpty(r, kind)).length;
}

// "For new patients" counts only when it does something: a template in 'all' mode, or a filled rule in
// procedure-specific mode (the mode alone defaults to 'all' on every action).
function newPatientSet(props, isEmail) {
  const mode = isEmail ? props.newPatientEmailMode : props.newPatientMode;
  if (mode === 'procedure_specific') return rulesIn(isEmail ? props.newPatientEmailRules : props.newPatientRules, 'newpatient') > 0;
  return isEmail
    ? has(props.newPatientEmailTemplateId) || isObject(props.newPatientEmailTemplateObj)
    : has(props.newPatientSmsTemplateId) || isObject(props.newPatientSmsTemplateObj);
}

function messagePreview(props, isEmail) {
  const parts = [];
  const n = rulesIn(isEmail ? props.emailRules : props.smsRules, isEmail ? 'email' : 'sms');
  if (n) parts.push(count(n, isEmail ? 'email rule' : 'text rule'));
  if (newPatientSet(props, isEmail)) parts.push('new patients');
  return parts.length ? ok(parts.join(' · ')) : warn();
}

function boardNameOf(props, opts) {
  if (isObject(props.boardObj) && str(props.boardObj.name)) return str(props.boardObj.name);
  if (typeof opts.boardName === 'function' && has(props.boardId)) {
    try {
      const name = str(opts.boardName(props.boardId));
      if (name) return name;
    } catch (e) {
      // a failing lookup falls through to the generic label
    }
  }
  return has(props.boardId) ? 'Board chosen' : '';
}

/**
 * The one line under a block's title: { text, tone } with tone 'ok' or 'warn'.
 * @param {Object} node   a classic builder node
 * @param {Object} [opts] isTrigger(type) — a trigger with no settings reads { text: '', tone: 'ok' } instead of
 *                        'Not set up yet'; boardName(boardId) — the Kanban board's name when the node has not
 *                        been opened yet (a loaded node carries only the board id)
 */
export function blockPreview(node, opts) {
  const n = isObject(node) ? node : {};
  const props = isObject(n.properties) ? n.properties : {};
  const o = isObject(opts) ? opts : {};

  switch (n.type) {
    case 'assigntask': {
      const c = rulesIn(props.taskRules, 'task');
      return c ? ok(count(c, 'task rule')) : warn();
    }
    case 'sms':
      return messagePreview(props, false);
    case 'email':
      return messagePreview(props, true);
    case 'kanban': {
      const board = boardNameOf(props, o);
      if (!board) return warn();
      const action = CARD_ACTIONS[str(props.cardAction)] || (isObject(props.cardActionObj) ? str(props.cardActionObj.text) : '');
      return ok(board + (action ? ' · ' + action : ''));
    }
    case 'wait': {
      const value = Number(props.durationValue);
      if (props.waitType === 'duration' && value > 0) {
        const unit = str(props.durationUnit) || 'hours';
        return ok('Wait ' + value + ' ' + (value === 1 ? unit.replace(/s$/, '') : unit));
      }
      if (props.waitType === 'timestamp' && str(props.waitUntil)) return ok('Wait until ' + str(props.waitUntil));
      return warn();
    }
    case 'sendform':
      return has(props.formId) ? ok('Form chosen') : warn();
    case 'slack': {
      if (str(props.slackChannelName)) return ok('#' + str(props.slackChannelName));
      return has(props.slackChannelId) ? ok('Channel chosen') : warn();
    }
    case 'xfollowups': {
      const c = Number(props.followupCount);
      return c > 0 ? ok('After ' + count(c, 'follow-up')) : warn();
    }
    case 'mangovoice': {
      const type = str(props.mangoEventType);
      return type ? ok(MANGO_EVENTS[type] || type) : warn();
    }
  }

  if (typeof o.isTrigger === 'function' && o.isTrigger(n.type)) return ok('');
  return warn();
}
